import {
	AccountSetupInfoDTO,
	AdAccountDTO,
	GoogleAdsAdAccountDTO,
	ProductAccountDTO
} from 'api-models'

import { ActivateAccountNotice } from './notices/activate-account'
import { CancellationNotice } from './notices/cancellation-notice'
import { GNoticeType } from './types'
import { InactiveAccountNotice } from './notices/inactive-account'
import { CompletedNotice } from './notices/completed-notice'
import { MccLinkRequestNotice } from './notices/mcc-link-request-notice'
import { AdAccounts } from '../../../services/user'
import { useAssetStore } from '../../../store'
import { toast } from 'react-toastify'
import ProgressCircle from '../../circle-progress-bar/circle-progress-bar'
import { SetupV2Banner } from './notices/setup-v2-banner'

interface BuildInSetupDemoDataNoticeProps {
	step?: AccountSetupInfoDTO['step']
	accountId?: string
	account?: ProductAccountDTO
	assetId?: string
	setupFlowV2Enabled?: boolean
}

export const buildInSetupDemoDataNotice = ({
	step,
	accountId,
	account,
	assetId,
	setupFlowV2Enabled = false
}: BuildInSetupDemoDataNoticeProps): any => {
	if (!step) return []

	// Show stepper banner for post-trial users mid-onboarding, even on demo data
	if (setupFlowV2Enabled && account) {
		const isSetupComplete = step === 'SETUP_COMPLETE'
		const isPostTrial =
			step === 'TRIAL_STARTED' ||
			step === 'WEBSITE_PROTECTED' ||
			step === 'AD_ACCOUNT_CONNECTED'

		if (!isSetupComplete && isPostTrial) {
			const bannerAssetId =
				assetId && assetId !== 'website-demo' ? assetId : undefined
			return [
				{
					key: 'setup-v2-banner',
					hideDismiss: true,
					containerClassName: '!rounded-[12px] !border-t-traffic',
					notice: (
						<SetupV2Banner account={account} assetId={bannerAssetId} />
					)
				}
			]
		}

		if (!isSetupComplete && assetId && assetId !== 'website-demo') {
			return [
				{
					key: 'setup-v2-banner',
					hideDismiss: true,
					containerClassName: '!rounded-[12px] !border-t-traffic',
					notice: <SetupV2Banner account={account} assetId={assetId} />
				}
			]
		}
	}

	// Activation banner for pre-trial users
	if (
		step === 'ACCOUNT_CREATED' ||
		step === 'PLAN_SELECTED' ||
		step === 'TRIAL_STARTED'
	) {
		// V2 post-trial without a real asset — still show stepper
		if (setupFlowV2Enabled && account && step === 'TRIAL_STARTED') {
			return [
				{
					key: 'setup-v2-banner',
					hideDismiss: true,
					containerClassName: '!rounded-[12px] !border-t-traffic',
					notice: <SetupV2Banner account={account} assetId={undefined} />
				}
			]
		}
		return [
			{
				key: 'in-setup-last-step',
				hideDismiss: true,
				notice: (
					<div className="flex items-center gap-2 w-full">
						<ActivateAccountNotice accountId={accountId} />
					</div>
				)
			}
		]
	}
	return []
}

export const buildInSetupTrackingCodeNotice = ({
	account,
	assetId,
	websiteStep
}: {
	account: ProductAccountDTO
	assetId: string
	websiteStep?: '2' | '3' | 'SETUP_COMPLETE'
}) => {
	return [
		{
			key: 'tracking-code',
			hideDismiss: true,
			containerClassName: '!rounded-[12px] !border-t-traffic',
			notice: (
				<SetupV2Banner
					account={account}
					assetId={assetId}
					websiteStep={websiteStep}
				/>
			)
		}
	]
}

export const buildInActiveNotice = ({
	accountId
}: {
	accountId: any
}): GNoticeType[] => {
	return [
		{
			key: 'in-active',
			hideDismiss: true,
			containerClassName: '!rounded-[12px] !border-t-traffic',
			notice: (
				<div className="flex items-center gap-2">
					<ProgressCircle step={1} />
					<InactiveAccountNotice accountId={accountId} />
				</div>
			)
		}
	]
}

export const buildCompletedNotice = (): GNoticeType[] => {
	return [
		{
			key: 'done',
			containerClassName: '!rounded-[12px] !border-t-traffic',
			notice: (
				<div className="flex items-center gap-2">
					<ProgressCircle step={1} />
					<CompletedNotice />
				</div>
			)
		}
	]
}
// DEV-2353 — derive the MCC banners from the lifecycle field (Field A) + dismissal
// timestamp (Field B). Up to three distinct banners can apply across a workspace's GADS
// accounts: the initial invite (PENDING/SKIPPED), the "link expired" re-prompt
// (LINK_REQUEST_CANCELLED) and the access-lost banner (ACCESS_LOST). A non-null
// mcc_banner_dismissed_at hides the dismissable banners (initial + expired); access-lost
// is not dismissable. PENDING_ACCEPTANCE / SENDING / SUPPRESSED render nothing.
export const buildMccLinkRequestNotice = (
	allAdAccounts: AdAccountDTO[],
	accountId?: string,
	onSent?: () => void
): GNoticeType[] => {
	const gadsAccounts = allAdAccounts.filter(
		(a: any) =>
			a?.platform === 'GADS' && a?.details?.mcc_support_connection !== 'LINKED'
	) as GoogleAdsAdAccountDTO[]

	if (!gadsAccounts.length) return []

	const isDismissed = (a: GoogleAdsAdAccountDTO) => !!a.details.mcc_banner_dismissed_at

	const initialAccounts = gadsAccounts.filter(
		(a) =>
			(a.details.mcc_support_connection === 'PENDING' ||
				a.details.mcc_support_connection === 'SKIPPED') &&
			!isDismissed(a)
	)
	const expiredAccounts = gadsAccounts.filter(
		(a) =>
			a.details.mcc_support_connection === 'LINK_REQUEST_CANCELLED' &&
			!isDismissed(a)
	)
	const accessLostAccounts = gadsAccounts.filter(
		(a) => a.details.mcc_support_connection === 'ACCESS_LOST' && !isDismissed(a)
	)

	// Dismiss a banner group: optimistically stamp mcc_banner_dismissed_at to hide it
	// immediately, persist via the dismissal endpoint, then refresh from the server.
	const dismissGroup = (accounts: GoogleAdsAdAccountDTO[]) => async () => {
		try {
			if (!accountId) return
			const ids = accounts.map((a) => a.id)
			const idSet = new Set(ids)

			const currentAdAccounts = useAssetStore.getState().adAccounts
			const updatedAdAccounts = currentAdAccounts.map((a) => {
				if (a.platform === 'GADS' && idSet.has(a.id)) {
					return {
						...a,
						details: {
							...(a as GoogleAdsAdAccountDTO).details,
							mcc_banner_dismissed_at: new Date()
						}
					}
				}
				return a
			})
			useAssetStore.getState().setAdAccounts(updatedAdAccounts)

			await AdAccounts.dismissMccBanner(accountId, ids)
			await useAssetStore.getState().refreshAdAccounts()
		} catch (e) {
			toast.error('Something went wrong')
			console.error(e)
		}
	}

	const notices: GNoticeType[] = []

	if (initialAccounts.length) {
		notices.push({
			key: 'mcc_link_request',
			type: 'brand',
			hideDismiss: true,
			containerClassName: '!rounded-[12px] !border-t-traffic',
			notice: (
				<MccLinkRequestNotice
					mccLinkInviteAdAccounts={initialAccounts}
					bannerType="INITIAL"
					onDismiss={dismissGroup(initialAccounts)}
					onSent={onSent}
				/>
			),
			onDismiss: dismissGroup(initialAccounts)
		})
	}

	if (expiredAccounts.length) {
		notices.push({
			key: 'mcc_link_expired',
			type: 'brand',
			hideDismiss: true,
			containerClassName: '!rounded-[12px] !border-t-traffic',
			notice: (
				<MccLinkRequestNotice
					mccLinkInviteAdAccounts={expiredAccounts}
					bannerType="EXPIRED"
					onDismiss={dismissGroup(expiredAccounts)}
					onSent={onSent}
				/>
			),
			onDismiss: dismissGroup(expiredAccounts)
		})
	}

	if (accessLostAccounts.length) {
		notices.push({
			key: 'mcc_access_lost',
			type: 'brand',
			hideDismiss: true,
			containerClassName: '!rounded-[12px] !border-t-traffic',
			notice: (
				<MccLinkRequestNotice
					mccLinkInviteAdAccounts={accessLostAccounts}
					bannerType="ACCESS_LOST"
					onSent={onSent}
				/>
			)
		})
	}

	return notices
}

export const buildCancellationNotice = (): GNoticeType[] => {
	return [
		{
			key: 'cancellation-banner',
			hideDismiss: true,
			containerClassName: '!rounded-[12px] !border-t-traffic',
			notice: <CancellationNotice />
		}
	]
}
